import { useState, useEffect } from 'react'
import { AudienceList } from 'hooks/queries/externalIntegrations/useMailchimpFetchAuidence'
import { MailchimpProfile } from 'api/clients/ExternalIntegrationsClient'
import Styles from './EmailBlock.module.scss'
import { SendToInput } from './inputs/SendToInput'
import { FromInput } from './inputs/FromInput'
import { SubjectInput } from './inputs/SubjectInput'
import { SendTimeInput } from './inputs/SendTimeInput'
import { MailchimpUser } from './types'
import { EmailNewsletterIcon } from 'blaze-ui/icons/20/EmailNewsLetterIcon'
import { useLocation } from 'react-router-dom'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Button } from 'blaze-ui/Button'
import { ScheduledPostStatusTag } from 'components/ScheduledPostStatusTag/ScheduledPostStatusTag'
import { EmailPublishingParams } from './hooks/useEmailPublishing'

type EmailBlockContentProps = {
  audienceLoading: boolean
  audienceError: Error | null
  audienceLists: AudienceList[]
  mailchimpProfile: MailchimpProfile
  handleOpenMailchimp: (params: {
    articleKey: string
    listId: string
    fromName: string
    replyTo: string
    subject: string
  }) => void
  handleSendNow: (params: {
    articleKey: string
    listId: string
    fromName: string
    replyTo: string
    time: string
    subject: string
  }) => void
  sendLoading: boolean
  openLoading: boolean
  subjectLine: string
  scheduledPostLoading: boolean
  handleUpdateSubjectLine: (subjectLine: string) => void
  listId?: string
  handleUpdateAudience: (listId: string) => void
  isPostAlreadyPublished?: boolean // New prop to disable send button when post is already published
  postAt?: string
  status?: PostStatusPropertyValue
  onScheduledTimeChange: (time: string | null, params: EmailPublishingParams) => void
  scheduledPostId?: number
  articleId: string
}

export function EmailBlockContent({
  audienceLoading,
  audienceError,
  audienceLists,
  mailchimpProfile,
  onScheduledTimeChange,
  handleSendNow,
  sendLoading,
  openLoading,
  subjectLine,
  scheduledPostLoading,
  handleUpdateSubjectLine,
  listId,
  handleUpdateAudience,
  isPostAlreadyPublished = false, // Default to false for backward compatibility
  status,
  postAt,
  scheduledPostId,
  articleId,
}: EmailBlockContentProps) {
  const [subject, setSubject] = useState(subjectLine || '')
  const [selectedContacts, setSelectedContacts] = useState<AudienceList | null>(null)
  const location = useLocation()
  const isSubjectEmpty = !subject || subject.trim() === ''

  useEffect(() => {
    if (subjectLine) {
      setSubject(subjectLine)
    }
  }, [subjectLine])

  useEffect(() => {
    if (!audienceLists.length) return

    if (listId) {
      const savedAudience = audienceLists.find((aud) => aud.id === listId)
      if (savedAudience && savedAudience.id !== selectedContacts?.id) {
        setSelectedContacts(savedAudience)
        return
      }
    }

    if (!selectedContacts) {
      setSelectedContacts(audienceLists[0])
    }
  }, [audienceLists, listId, selectedContacts])

  const getArticleKey = () => {
    const path = location.pathname
    const lastSegment = path.split('/').pop() || ''
    const parts = lastSegment.split('-')
    return parts[parts.length - 1]
  }

  const onSendNowClick = () => {
    const articleKey = getArticleKey()
    const listId = selectedContacts?.id || ''
    const email = (mailchimpProfile as MailchimpUser)?.email || ''
    const fromName = (mailchimpProfile as MailchimpUser)?.account_name || ''
    const time = 'now'
    handleSendNow({ articleKey, listId, fromName, replyTo: email, time, subject })
  }

  const handleSubjectChange = (subject: string) => {
    setSubject(subject)
    handleUpdateSubjectLine(subject)
  }

  const handleContactsChange = (contacts: AudienceList) => {
    if (contacts.id !== selectedContacts?.id) {
      setSelectedContacts(contacts)
      handleUpdateAudience(contacts.id)
    }
  }

  const getButtonText = () => {
    if (isPostAlreadyPublished) return 'Already Published'
    return 'Send Now'
  }

  const handleSchedule = (time: string | null) => {
    const listId = selectedContacts?.id || ''
    const fromName = (mailchimpProfile as MailchimpUser)?.account_name || ''
    const email = (mailchimpProfile as MailchimpUser)?.email || ''
    onScheduledTimeChange(time, { listId, fromName, replyTo: email, subject })
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.header}>
        <div className={Styles.titleContainer}>
          <span className={Styles.title}>
            <EmailNewsletterIcon className={Styles.emailNewsletterIcon} />
            Email Newsletter
          </span>
          <ScheduledPostStatusTag
            scheduledPostId={scheduledPostId}
            articleId={articleId}
            overrides={{
              [PostStatusPropertyValue.Posted]: 'Sent',
            }}
          />
        </div>
        <div className={Styles.actions}>
          <Button
            variant="primary"
            onPress={onSendNowClick}
            isDisabled={sendLoading || isSubjectEmpty || isPostAlreadyPublished}
            data-testid="send-action-button"
          >
            {getButtonText()}
          </Button>
        </div>
      </div>
      <div className={Styles.formGrid}>
        <SendToInput
          audienceLoading={audienceLoading}
          audienceError={audienceError}
          audienceLists={audienceLists}
          selectedContacts={selectedContacts}
          onContactsChange={handleContactsChange}
          data-testid="send-to-input"
        />
        <FromInput mailchimpProfile={mailchimpProfile} />
        <SubjectInput subject={subject} onSubjectChange={handleSubjectChange} isLoading={scheduledPostLoading} />
        <SendTimeInput scheduledTime={postAt} onScheduledTimeChange={handleSchedule} />
      </div>
    </div>
  )
}
